export const TEAM_LINK_OPTIONS = [
  "opponent",
  "selected_team",
];

// "opponent_of_previous" was the old value; a step's opponent link now always
// means the opponent of the selected team, regardless of the previous step.
// Map the legacy value so saved templates keep working.
const LEGACY_TEAM_LINKS = {
  opponent_of_previous: "opponent",
};

export const normalizeTeamLink = (value) => {
  const mapped = LEGACY_TEAM_LINKS[value] || value;
  return TEAM_LINK_OPTIONS.includes(mapped) ? mapped : "selected_team";
};

// Typeahead items carry a "type". "parameter" ids resolve through player stats
// and are sent as action_ids (unchanged). "action" ids are raw
// platform_api_action ids and must go in event_action_ids so they don't collide
// with the overlapping parameter id space. Anything else — including legacy
// saved templates that predate the field — is treated as a parameter.
export const normalizeSequenceEventType = (value) =>
  value === "action" ? "action" : "parameter";

const STEP_ID_FIELDS = [
  "action_ids",
  "set_piece_ids",
  "attack_ids",
  "attack_option_ids",
];

const toSnakeCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[+]/g, " plus ")
    .replace(/[-]/g, " minus ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toPositiveInteger = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) {
    return null;
  }
  return Math.floor(number);
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getFirstAvailableField = (item = {}, fields = []) => {
  const source = item || {};
  return fields.find(
    (field) => source[field] !== undefined && source[field] !== null
  );
};

export const parseSequenceIds = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0);
  }

  return String(value || "")
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0);
};

export const parseActionIds = parseSequenceIds;

const inferStepIdField = (group = {}, defaultField = "action_ids") => {
  const groupTitle = String(group.title || group.name || "").toLowerCase();

  if (group.step_id_field && STEP_ID_FIELDS.includes(group.step_id_field)) {
    return group.step_id_field;
  }
  if (groupTitle.includes("set piece")) {
    return "set_piece_ids";
  }
  if (groupTitle.includes("attack option")) {
    return "attack_option_ids";
  }
  if (groupTitle.includes("attack")) {
    return "attack_ids";
  }

  return defaultField;
};

export const buildSequenceEventOptions = (
  parameterGroups = [],
  defaultStepIdField = "action_ids"
) => {
  return (Array.isArray(parameterGroups) ? parameterGroups : [])
    .flatMap((group) =>
      (Array.isArray(group?.actions) ? group.actions : []).map((action) => ({
        id: `${inferStepIdField(group, defaultStepIdField)}-${action.id}`,
        step_id: action.id,
        step_id_field: inferStepIdField(group, defaultStepIdField),
        group: group.title,
        kind: toSnakeCase(action.title || action.name || action.parameter_name),
        label: action.title || action.name || action.parameter_name || `Action ${action.id}`,
        source: inferStepIdField(group, defaultStepIdField),
      }))
    )
    .filter((option) => option.step_id && option.kind);
};

export const buildSequenceEventOptionsFromApiMarkers = (markers = []) => {
  return (Array.isArray(markers) ? markers : [])
    .map((marker) => {
      const configuredStepIdField = STEP_ID_FIELDS.includes(marker?.step_id_field)
        ? marker.step_id_field
        : null;
      const directStepIdField =
        configuredStepIdField ||
        getFirstAvailableField(marker, STEP_ID_FIELDS) ||
        (marker?.action_id !== undefined || marker?.actionId !== undefined
          ? "action_ids"
          : null);
      const stepIdField = directStepIdField || "action_ids";
      const stepIdValue = firstDefined(
        marker?.[stepIdField],
        marker?.action_id,
        marker?.actionId,
        marker?.step_id,
        marker?.api_marker_id,
        marker?.id
      );
      const stepIds = parseSequenceIds(stepIdValue);
      const label = firstDefined(
        marker?.label,
        marker?.title,
        marker?.name,
        marker?.action,
        marker?.parameter_name,
        marker?.display_name,
        stepIds.length ? `${stepIdField}: ${stepIds.join(", ")}` : ""
      );
      const group = firstDefined(
        marker?.group,
        marker?.group_title,
        marker?.category,
        marker?.category_name,
        marker?.parameter_group,
        marker?.section
      );

      if (!label || stepIds.length === 0) {
        return null;
      }

      const type = normalizeSequenceEventType(marker?.type);

      return {
        // Parameter and raw-action ids can overlap. Include the source type so
        // React receives a stable, globally unique key when the catalog is
        // filtered and never leaves stale option rows in the dropdown.
        id: [type, stepIdField, stepIds.join("-"), marker?.id ?? label].join("-"),
        step_id: stepIds[0],
        step_ids: stepIds,
        step_id_field: stepIdField,
        group,
        kind: marker?.kind || marker?.slug || toSnakeCase(label),
        label,
        source: stepIdField,
        type,
      };
    })
    .filter(Boolean);
};

export const createSequenceStepFromEventOption = (
  option = {},
  fallbackId,
  teamLink = "any"
) => {
  const stepIds = parseSequenceIds(option.step_ids ?? option.step_id);

  return {
    id: fallbackId,
    kind: option.kind || "custom_event_name",
    team_link: normalizeTeamLink(teamLink),
    min_count: 1,
    stepIdField: STEP_ID_FIELDS.includes(option.step_id_field)
      ? option.step_id_field
      : "action_ids",
    idsInput: stepIds.join(","),
    exclude: false,
    eventType: normalizeSequenceEventType(option.type),
    sourceLabel: option.label || "",
    sourceGroup: option.group || "",
  };
};

export const moveItem = (items, fromIndex, toIndex) => {
  if (!Array.isArray(items)) {
    return [];
  }

  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
};

export const normalizeSequenceStep = (step = {}) => {
  const minCount = toPositiveInteger(step.min_count);
  const teamLink = normalizeTeamLink(step.team_link);
  const stepIdField = STEP_ID_FIELDS.includes(step.stepIdField)
    ? step.stepIdField
    : STEP_ID_FIELDS.find((field) => step[field] !== undefined) || "action_ids";
  const stepIds = parseSequenceIds(step[stepIdField] ?? step.idsInput);

  if (!minCount || stepIds.length === 0) {
    return null;
  }

  const normalizedStep = {
    team_link: teamLink,
    min_count: minCount,
  };

  // Raw actions (typeahead type "action") go in event_action_ids; everything
  // else keeps its existing field. Parameters stay on action_ids exactly as
  // before. Only action_ids is rerouted — set_piece_ids/attack_ids are untouched.
  if (stepIdField === "action_ids" && step.eventType === "action") {
    normalizedStep.event_action_ids = stepIds;
  } else {
    normalizedStep[stepIdField] = stepIds;
  }

  if (step.exclude === true) {
    normalizedStep.exclude = true;
  }

  return normalizedStep;
};

export const buildSequenceSearchPayload = ({
  competitionId,
  seasonId,
  matchId,
  matchIds,
  teamId,
  teamIds,
  playerId,
  playerIds,
  maxEventsBetween,
  minSequenceCountPerMatch,
  maxSequenceCountPerMatch,
  steps = [],
  sequenceView = false,
  immediateEventsOnly = false,
  patternAnalysis = false,
}) => {
  const competition_id = toPositiveInteger(competitionId);
  const season_id = toPositiveInteger(seasonId);
  const max_events_between = toPositiveInteger(maxEventsBetween);
  const min_sequence_count_per_match = toPositiveInteger(minSequenceCountPerMatch);
  const max_sequence_count_per_match = toPositiveInteger(maxSequenceCountPerMatch);
  const normalizedSteps = steps
    .map((step) => normalizeSequenceStep(step))
    .filter(Boolean);

  const payload = {
    competition_id,
    season_id,
    immediate_events_only: immediateEventsOnly === true,
    steps: normalizedSteps,
  };

  const normalizedPlayerIds = parseSequenceIds(
    playerIds === undefined || playerIds === null ? playerId : playerIds
  );
  // Teams mirror players: a single selection stays as the scalar team_id (kept
  // for backward compatibility with existing callers), several become team_ids.
  const normalizedTeamIds = parseSequenceIds(
    teamIds === undefined || teamIds === null ? teamId : teamIds
  );

  const optionalIds = {
    match_id: toPositiveInteger(matchId),
    team_id: normalizedTeamIds.length === 1 ? normalizedTeamIds[0] : null,
    player_id:
      normalizedPlayerIds.length === 1 ? normalizedPlayerIds[0] : null,
  };

  Object.entries(optionalIds).forEach(([key, value]) => {
    if (value) {
      payload[key] = value;
    }
  });

  if (normalizedTeamIds.length > 1) {
    payload.team_ids = normalizedTeamIds;
  }
  if (normalizedPlayerIds.length > 1) {
    payload.player_ids = normalizedPlayerIds;
  }
  if (max_events_between) {
    payload.max_events_between = max_events_between;
  }
  // "From": only send when > 1 (1 = no lower bound).
  if (min_sequence_count_per_match && min_sequence_count_per_match > 1) {
    payload.min_sequence_count_per_match = min_sequence_count_per_match;
  }
  // "To" is optional and meaningful at any value (incl. 1 = appears exactly once
  // when no "From" set); send whenever a positive number is given.
  if (max_sequence_count_per_match) {
    payload.max_sequence_count_per_match = max_sequence_count_per_match;
  }
  const normalizedMatchIds = parseSequenceIds(matchIds);
  if (normalizedMatchIds.length > 0) {
    payload.match_ids = normalizedMatchIds;
    delete payload.match_id;
  }
  if (sequenceView === true || sequenceView === "sequence") {
    payload.sequence_view = true;
  }

  // Opponent Pattern Analysis: the backend auto-detects repeating patterns. It
  // requires this flag (empty steps alone is a 422) and ignores the
  // timeline-only fields, so drop them to keep the request clean.
  if (patternAnalysis) {
    payload.pattern_analysis = true;
    delete payload.immediate_events_only;
    delete payload.sequence_view;
  }

  return payload;
};

const getMatchKey = (result = {}) => {
  const match = result.match || result.match_info || {};
  return String(
    firstDefined(
      result.match_id,
      match.id,
      match.match_id,
      result.matchId,
      "unknown"
    )
  );
};

const getMatchInfo = (result = {}) => {
  const match = result.match || result.match_info || {};
  return {
    id: firstDefined(result.match_id, match.id, match.match_id, result.matchId),
    home_team: firstDefined(match.home_team, match.homeTeam, match.home?.name),
    away_team: firstDefined(match.away_team, match.awayTeam, match.away?.name),
    score: firstDefined(match.score, match.result),
    competition: firstDefined(match.competition, match.competition_name),
    date: firstDefined(match.date, match.match_date),
  };
};

const toArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value === undefined || value === null || value === "") {
    return [];
  }
  return [value];
};

export const groupSequenceResultsByMatch = (results = []) => {
  const grouped = new Map();

  toArray(results).forEach((result) => {
    const matchKey = getMatchKey(result);
    const existingGroup =
      grouped.get(matchKey) || {
        matchKey,
        match: getMatchInfo(result),
        results: [],
        sequence_ids: [],
        markers: [],
        players: [],
        matched_steps: [],
      };

    existingGroup.results.push(result);
    existingGroup.sequence_ids.push(
      ...toArray(result.sequence_ids ?? result.sequence_id)
    );
    existingGroup.markers.push(...toArray(result.markers));
    existingGroup.players.push(...toArray(result.players));
    existingGroup.matched_steps.push(
      ...toArray(result.matched_steps ?? result.steps)
    );

    grouped.set(matchKey, existingGroup);
  });

  return Array.from(grouped.values()).map((group) => ({
    ...group,
    sequence_ids: Array.from(new Set(group.sequence_ids)),
  }));
};

const getMarkerRecordId = (marker) => {
  if (marker && typeof marker === "object") {
    return String(
      firstDefined(marker.id, marker.marker_id, marker.event_id) ?? ""
    );
  }
  return String(marker ?? "");
};

const getMarkerRecordLabel = (marker = {}) =>
  String(
    firstDefined(
      marker.parameter_name,
      marker.action,
      marker.name,
      marker.title,
      marker.label
    ) ?? ""
  ).trim();

// Title for a merged sequence video: "<first kept marker> - <last kept marker>".
// Markers are ordered by their second when available so "first"/"last" follow
// the actual play order, not the API response order.
export const buildSequenceMarkerPairTitle = (
  markerRecords = [],
  keptMarkerIds = []
) => {
  const keptIdSet = new Set(
    (Array.isArray(keptMarkerIds) ? keptMarkerIds : [])
      .map((markerId) => String(markerId ?? "").trim())
      .filter(Boolean)
  );

  const keptRecords = (Array.isArray(markerRecords) ? markerRecords : []).filter(
    (marker) => {
      const markerId = getMarkerRecordId(marker);
      return markerId && (keptIdSet.size === 0 || keptIdSet.has(markerId));
    }
  );

  if (keptRecords.length === 0) {
    return "";
  }

  const allHaveSeconds = keptRecords.every((marker) =>
    Number.isFinite(Number(marker?.second))
  );
  const orderedRecords = allHaveSeconds
    ? [...keptRecords].sort((left, right) => Number(left.second) - Number(right.second))
    : keptRecords;

  const firstLabel = getMarkerRecordLabel(orderedRecords[0]);
  const lastLabel = getMarkerRecordLabel(
    orderedRecords[orderedRecords.length - 1]
  );

  if (!firstLabel || !lastLabel) {
    return "";
  }

  return orderedRecords.length === 1 || firstLabel === lastLabel
    ? firstLabel
    : `${firstLabel} - ${lastLabel}`;
};

export const getResultDuration = (result = {}) =>
  firstDefined(
    result.duration_seconds,
    result.chain_duration_seconds,
    result.duration,
    result.end_second !== undefined && result.start_second !== undefined
      ? Number(result.end_second) - Number(result.start_second)
      : undefined
  );

export const SEQUENCE_VIDEO_MODES = ["separate", "sequence"];

const toPlainObjectOrNull = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

// Snapshot of the search form persisted as a saved filter template. Match
// preset / manually picked matches / date range are intentionally left out:
// the available match list is refetched per scope and stored match ids would
// go stale.
export const buildSequenceTemplateFilters = ({
  competition,
  season,
  teams,
  selectedPlayers,
  videoPlaybackMode,
  immediateEventsOnly,
  maxEventsBetween,
  steps,
} = {}) => ({
  competition: toPlainObjectOrNull(competition),
  season: toPlainObjectOrNull(season),
  teams: Array.isArray(teams)
    ? teams.filter((teamItem) => toPlainObjectOrNull(teamItem))
    : [],
  selectedPlayers: Array.isArray(selectedPlayers)
    ? selectedPlayers.filter((player) => toPlainObjectOrNull(player))
    : [],
  videoPlaybackMode: SEQUENCE_VIDEO_MODES.includes(videoPlaybackMode)
    ? videoPlaybackMode
    : SEQUENCE_VIDEO_MODES[0],
  immediateEventsOnly: immediateEventsOnly === true,
  maxEventsBetween:
    maxEventsBetween === undefined || maxEventsBetween === null
      ? ""
      : String(maxEventsBetween),
  steps: Array.isArray(steps)
    ? steps.filter((step) => toPlainObjectOrNull(step))
    : [],
});

const parseTemplateStep = (step, fallbackId) => {
  const source = toPlainObjectOrNull(step);

  if (!source) {
    return null;
  }

  const stepIdField = STEP_ID_FIELDS.includes(source.stepIdField)
    ? source.stepIdField
    : "action_ids";
  const stepIds = parseSequenceIds(source.idsInput ?? source[stepIdField]);

  if (stepIds.length === 0) {
    return null;
  }

  return {
    id: String(source.id || fallbackId),
    kind: source.kind || "custom_event_name",
    team_link: normalizeTeamLink(source.team_link),
    min_count: toPositiveInteger(source.min_count) || 1,
    stepIdField,
    idsInput: stepIds.join(","),
    exclude: source.exclude === true,
    eventType: normalizeSequenceEventType(source.eventType),
    sourceLabel: String(source.sourceLabel || ""),
    sourceGroup: String(source.sourceGroup || ""),
  };
};

// Inverse of buildSequenceTemplateFilters: sanitizes a stored template (which
// may come from an older build or have been edited server-side) back into
// state the page can trust. createId supplies ids for steps missing one.
export const parseSequenceTemplateFilters = (filters, createId = () => "") => {
  const source = toPlainObjectOrNull(filters) || {};

  return {
    competition: toPlainObjectOrNull(source.competition),
    season: toPlainObjectOrNull(source.season),
    // Accept the legacy single `team` key from templates saved before teams
    // became multi-select.
    teams: Array.isArray(source.teams)
      ? source.teams.filter((teamItem) => toPlainObjectOrNull(teamItem))
      : toPlainObjectOrNull(source.team)
        ? [source.team]
        : [],
    selectedPlayers: Array.isArray(source.selectedPlayers)
      ? source.selectedPlayers.filter((player) => toPlainObjectOrNull(player))
      : [],
    videoPlaybackMode: SEQUENCE_VIDEO_MODES.includes(source.videoPlaybackMode)
      ? source.videoPlaybackMode
      : SEQUENCE_VIDEO_MODES[0],
    immediateEventsOnly: source.immediateEventsOnly === true,
    maxEventsBetween:
      source.maxEventsBetween === undefined || source.maxEventsBetween === null
        ? ""
        : String(source.maxEventsBetween),
    steps: (Array.isArray(source.steps) ? source.steps : [])
      .map((step) => parseTemplateStep(step, createId()))
      .filter(Boolean),
  };
};

export const hasSequenceTemplateContent = (filters) => {
  const source = toPlainObjectOrNull(filters) || {};

  return Boolean(
    toPlainObjectOrNull(source.competition) ||
      toPlainObjectOrNull(source.season) ||
      (Array.isArray(source.teams) && source.teams.length > 0) ||
      toPlainObjectOrNull(source.team) ||
      (Array.isArray(source.selectedPlayers) &&
        source.selectedPlayers.length > 0) ||
      (Array.isArray(source.steps) && source.steps.length > 0) ||
      source.immediateEventsOnly === true ||
      String(source.maxEventsBetween ?? "").trim() !== ""
  );
};
